import React from "react";
import { FiRefreshCw, FiZap } from "react-icons/fi";
import { InsightType } from "../../pages/compose/ScheduleReviewPage";
import InsightTypeCard from "./InsightTypeCard";
import { getIntegrationIconSrc } from "../../utils/integrationIcons";

// Type-safe icon wrappers
const RefreshIcon = FiRefreshCw as React.FC<React.SVGProps<SVGSVGElement>>;
const ZapIcon = FiZap as React.FC<React.SVGProps<SVGSVGElement>>;

interface Integration {
  id: string;
  provider: string;
  name: string;
}

interface AutomatedWorkflowConfigProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  workflowName: string;
  onWorkflowNameChange: (name: string) => void;
  integrations: Integration[];
  selectedDataSources: string[];
  onDataSourceToggle: (provider: string) => void;
  insightTypes: InsightType[];
  selectedInsightTypes: string[];
  onInsightTypeToggle: (id: string) => void;
}

/**
 * AutomatedWorkflowConfig - Configuration panel for automated workflows
 */
const AutomatedWorkflowConfig: React.FC<AutomatedWorkflowConfigProps> = ({
  enabled,
  onToggle,
  workflowName,
  onWorkflowNameChange,
  integrations,
  selectedDataSources,
  onDataSourceToggle,
  insightTypes,
  selectedInsightTypes,
  onInsightTypeToggle,
}) => {
  return (
    <div className="su-section su-automated-workflow-config">
      {/* Toggle Header */}
      <div className="su-workflow-toggle-row">
        <div className="su-workflow-toggle-left">
          <RefreshIcon className="w-5 h-5 su-workflow-toggle-icon" />
          <div className="su-workflow-toggle-text">
            <span className="su-workflow-toggle-title">Save as Automated Workflow</span>
            <span className="su-workflow-toggle-description">
              Automatically regenerate insights and send on schedule
            </span>
          </div>
        </div>
        <button
          className={`su-workflow-toggle ${enabled ? "active" : ""}`}
          onClick={() => onToggle(!enabled)}
          aria-label="Toggle automated workflow"
        >
          <span className="su-workflow-toggle-thumb" />
        </button>
      </div>

      {/* Expanded Content */}
      {enabled && (
        <div className="su-workflow-config-content">
          {/* Workflow Name */}
          <div className="su-workflow-field">
            <label className="su-workflow-field-label">WORKFLOW NAME</label>
            <input
              type="text"
              className="su-workflow-input"
              value={workflowName}
              onChange={(e) => onWorkflowNameChange(e.target.value)}
              placeholder="Enter workflow name"
            />
          </div>

          {/* Data Sources */}
          <div className="su-workflow-field">
            <label className="su-workflow-field-label">DATA SOURCES</label>
            <div className="su-workflow-data-sources">
              {integrations.map((integration) => {
                const isSelected = selectedDataSources.includes(integration.provider);
                const iconSrc = getIntegrationIconSrc(integration.provider);

                return (
                  <button
                    key={integration.id}
                    className={`su-workflow-data-source ${isSelected ? "selected" : ""}`}
                    onClick={() => onDataSourceToggle(integration.provider)}
                  >
                    {iconSrc ? (
                      <img
                        src={iconSrc}
                        alt={integration.name}
                        className="su-workflow-data-source-icon"
                      />
                    ) : (
                      <span className="su-workflow-data-source-placeholder">
                        {integration.name.charAt(0)}
                      </span>
                    )}
                    <span>{integration.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Insight Types */}
          <div className="su-workflow-field">
            <label className="su-workflow-field-label">INSIGHT TYPES TO REGENERATE</label>
            <span className="su-workflow-field-sublabel">
              Select which metrics to automatically pull each time
            </span>
            <div className="su-workflow-insight-grid">
              {insightTypes.map((insight) => (
                <InsightTypeCard
                  key={insight.id}
                  insight={insight}
                  isSelected={selectedInsightTypes.includes(insight.id)}
                  onToggle={() => onInsightTypeToggle(insight.id)}
                />
              ))}
            </div>
          </div>

          {/* Info Banner */}
          <div className="su-workflow-info-banner">
            <ZapIcon className="w-5 h-5 su-workflow-info-icon" />
            <div className="su-workflow-info-text">
              <span className="su-workflow-info-title">How workflows work</span>
              <span className="su-workflow-info-description">
              Each week, we&apos;ll automatically pull fresh data from your selected integrations,
              regenerate the insights you&apos;ve chosen, update your email with the new metrics,
              and send it to your recipients.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomatedWorkflowConfig;

